import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Nav,
  NavItem,
  NavLink,
  Input,
  Label,
  Table
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { postDataAPI } from "../../helpers/phpbackend_helper";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const notifyCustom = (msg, type) =>
  toast(msg, {
    position: "top-right",
    hideProgressBar: true,
    className: `bg-${type} text-white`,
  });

const capitalizeWords = (str) => {
  if (!str) return "";
  return str.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
};

const TAB_COMPLETED = "Completed";
const TAB_ASSIGNED = "Assigned";
const TAB_NON_ASSIGNED = "Non assigned";
const TAB_SCENARIO_ASSIGNED = "Scenario assigned";
const TAB_SCENARIO_NON_ASSIGNED = "Scenario Non Assigned";

const isScenarioCard = (card) => Number(card?.is_scenario) === 1;

const targetDurationOptions = ["1 day", "3 days", "7 days", "14 days"];
const specialTargetDuration = "0 days";
const increasePeriodOptions = ["+10%", "+15%", "+20%", "+25%", "+50%"];

const AssignTrainingsModal = ({
  isOpen,
  toggle,
  server,
  updateSingleServer
}) => {
  
  const { currentOutlet } = useSelector((state) => ({
    currentOutlet: state.UserOutletGroup.currentOutlet
  }));
  const currency = currentOutlet?.currency || "AED";

  const [flashcards, setFlashcards] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_ASSIGNED);
  const [completionDate, setCompletionDate] = useState(null);
  const [targetDuration, setTargetDuration] = useState("14 days");
  const [increasePeriod, setIncreasePeriod] = useState("+20%");
  const [isSpecialOfTheDay, setIsSpecialOfTheDay] = useState(false);
  const [isNoTarget, setIsNoTarget] = useState(false);
  const [step, setStep] = useState(1);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [impactData, setImpactData] = useState([]);

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 1);
    return date;
  }, []);

  const defaultDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  }, []);

  const handleSpecialOfTheDayChange = (checked) => {
    setIsSpecialOfTheDay(checked);
    if (checked) {
      setIsNoTarget(false);
      setCompletionDate(today);
      setTargetDuration(specialTargetDuration);
      setIncreasePeriod("+20%");
    } else {
      setCompletionDate(defaultDate);
      setTargetDuration("14 days");
    }
  };

  const handleNoTargetChange = (checked) => {
    setIsNoTarget(checked);
    if (checked) {
      setIsSpecialOfTheDay(false);
      setCompletionDate(defaultDate);
      setTargetDuration("14 days");
      setIncreasePeriod("+20%");
    }
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ✅ Fetch flashcards and scenarios
  const fetchFlashcards = useCallback(() => {
    if (!server?.server_id) return;
    setLoading(true);

    Promise.all([
      postDataAPI({
        serviceName: "item_data",
        op: "server_coaching_outlet_items",
        server_id: server.server_id,
        display_classcification: "Yes",
        type: "Flash",
        status: "Active",
        load: 1,
        is_show_all: true,
        course_id: "",
        flash_status: "Complete"
      }),
      postDataAPI({
        serviceName: "item_data",
        op: "list_scenarios",
        server_id: server.server_id,
        load: 1,
        is_show_all: true
      })
    ])
      .then(([dishRes, scenarioRes]) => {
        const dishes = dishRes?.status === "ok" ? (dishRes.data.server_data || []) : [];
        const scenarios = scenarioRes?.status === "ok" ? (scenarioRes.data.server_data || []) : [];
        setFlashcards([
          ...dishes.map((card) => ({ ...card, is_scenario: Number(card.is_scenario) === 1 ? 1 : 0 })),
          ...scenarios.map((card) => ({ ...card, is_scenario: 1 }))
        ]);
      })
      .catch((err) => {
        console.error("Error loading trainings:", err);
      })
      .finally(() => setLoading(false));
  }, [server?.server_id]);

  // ✅ Init modal data
  useEffect(() => {
    if (isOpen && server) {
      fetchFlashcards();

      // Start with no cards selected; user chooses which assigned/completed cards to remove.
      // setSelected([]);
      setCompletionDate(defaultDate);
      setTargetDuration("14 days");
      setIncreasePeriod("+20%");
      setIsSpecialOfTheDay(false);
      setIsNoTarget(false);
      setStep(1);
      setImpactData([]);
      setLoadingImpact(false);

      // Pre-select already assigned cards
      // setSelected(
      //   Array.isArray(server.flash_cards)
      //     ? server.flash_cards.map(String)
      //     : []
      // );

      // Start with no cards selected; user chooses which assigned/completed cards to remove.
      setSelected([]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    const day = date.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const targetDurationLabel = isSpecialOfTheDay ? "Today" : capitalizeWords(targetDuration);
  const baselinePeriodLabel = isSpecialOfTheDay ? "Previous Day" : capitalizeWords(targetDuration);

  const selectedCardsData = useMemo(() => {
    return flashcards.filter(card => selected.includes(String(card.flash_id)));
  }, [flashcards, selected]);

  const tableRows = useMemo(() => {
    let totalRev = 0;
    let totalRevenueUplift = 0;

    const rows = selectedCardsData.map((card, index) => {
      const apiItem = Array.isArray(impactData)
        ? impactData.find(
            (item) => String(item.flash_id) === String(card.flash_id),
          )
        : null;

      const previousUnits = apiItem && apiItem.prev_qty !== undefined ? Number(apiItem.prev_qty) : 0;
      const targetUnits = apiItem && apiItem.target_item_sold_ceiling !== undefined
        ? Number(apiItem.target_item_sold_ceiling) || 0
        : 0;
      const expectedLift = apiItem && apiItem.item_sold_uplift_ceiling !== undefined
        ? Number(apiItem.item_sold_uplift_ceiling) || 0
        : 0;
      const targetRevenue = apiItem && apiItem.target_revenue !== undefined
        ? Number(apiItem.target_revenue) || 0
        : 0;
      const revenueUplift = apiItem && apiItem.revenue_uplift !== undefined
        ? Number(apiItem.revenue_uplift) || 0
        : 0;

      totalRev += targetRevenue;
      totalRevenueUplift += revenueUplift;

      return {
        index: index + 1,
        name: card.item_name,
        previousUnits,
        targetUnits,
        expectedLift,
        targetRevenue
      };
    });

    return { rows, totalRev, totalRevenueUplift };
  }, [selectedCardsData, impactData]);

  const totalExpectedRevenue = tableRows.totalRev;

  const assignedIds = useMemo(
    () => new Set(Array.isArray(server?.flash_cards) ? server.flash_cards.map(String) : []),
    [server]
  );

  const quizMap = useMemo(
    () =>
      new Map(
        Array.isArray(server?.quiz_data)
          ? server.quiz_data
              .filter((item) => assignedIds.has(String(item.flash_id)))
              .map((item) => [String(item.flash_id), item])
          : []
      ),
    [server?.quiz_data, assignedIds]
  );

  const categorizedCards = useMemo(() => {
    const groups = {
      [TAB_COMPLETED]: [],
      [TAB_ASSIGNED]: [],
      [TAB_NON_ASSIGNED]: [],
      [TAB_SCENARIO_ASSIGNED]: [],
      [TAB_SCENARIO_NON_ASSIGNED]: []
    };

    flashcards.forEach((card) => {
      const id = String(card.flash_id);
      const isCompleted = quizMap.has(id);
      const isAssigned = assignedIds.has(id);
      const scenario = isScenarioCard(card);

      if (isCompleted) {
        groups[TAB_COMPLETED].push(card);
      } else if (scenario) {
        if (isAssigned) {
          groups[TAB_SCENARIO_ASSIGNED].push(card);
        } else {
          groups[TAB_SCENARIO_NON_ASSIGNED].push(card);
        }
      } else if (isAssigned) {
        groups[TAB_ASSIGNED].push(card);
      } else {
        groups[TAB_NON_ASSIGNED].push(card);
      }
    });

    return groups;
  }, [flashcards, assignedIds, quizMap]);

  const tabConfig = [
    { key: TAB_COMPLETED, label: "Completed" },
    { key: TAB_ASSIGNED, label: "Assigned" },
    { key: TAB_NON_ASSIGNED, label: "Non assigned" },
    { key: TAB_SCENARIO_ASSIGNED, label: "Scenario assigned" },
    { key: TAB_SCENARIO_NON_ASSIGNED, label: "Scenario Non Assigned" }
  ];

  const isDishAssignTab = activeTab === TAB_NON_ASSIGNED;
  const isScenarioAssignTab = activeTab === TAB_SCENARIO_NON_ASSIGNED;
  const isAssignTab = isDishAssignTab || isScenarioAssignTab;

  const visibleCards = categorizedCards[activeTab] || [];

  // ✅ Toggle card
  const toggleCard = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  // ✅ Assign / unassign API
  const handleAssign = () => {
    if (!selected.length) {
      alert("Please select at least one flashcard");
      return;
    }

    if (isDishAssignTab && !completionDate) {
      alert("Please select a completion date for the assigned flashcards");
      return;
    }

    setSaving(true);

    const currentAssigned = Array.isArray(server.flash_cards)
      ? server.flash_cards.map(String)
      : [];

    const flash_card_id =
      isAssignTab
        ? Array.from(new Set([...currentAssigned, ...selected]))
        : currentAssigned.filter((id) => !selected.includes(id));

    postDataAPI({
      serviceName: "server_data",
      op: "create_update_server_flash_cards",
      server_id: server.server_id,
      flash_card_id,
      ...(isDishAssignTab && completionDate
        ? {
            target_complete_date: formatLocalDate(completionDate),
            target_duration: isNoTarget
              ? null
              : isSpecialOfTheDay
                ? "0"
                : (targetDuration ? targetDuration.replace(/\D/g, "") : ""),
            increase_period: isNoTarget
              ? null
              : (increasePeriod ? increasePeriod.replace(/\D/g, "") : "")
          }
        : isScenarioAssignTab
          ? {
              target_complete_date: null,
              target_duration: null,
              increase_period: null
            }
          : {}),
    })
      .then((res) => {
        if (res.status === "ok") {
          const updatedFlashCards = flash_card_id;
          const updatedQuizData = (server.quiz_data || []).filter((q) =>
            flash_card_id.includes(String(q.flash_id))
          );

          if (updateSingleServer) {
            updateSingleServer(server.server_id, {
              flash_cards: updatedFlashCards,
              quiz_data: updatedQuizData
            });
          }

          notifyCustom(
            res.message ||
              (isAssignTab
                ? isScenarioAssignTab || isNoTarget
                  ? "Trainings assigned successfully!"
                  : "Trainings & Targets assigned successfully!"
                : "Trainings unassigned successfully!"),
            "success"
          );

          if (isAssignTab) {
            toggle();
          }

          setSelected([]);
          setCompletionDate(defaultDate);
          setTargetDuration("14 days");
          setIncreasePeriod("+20%");
          setIsSpecialOfTheDay(false);
          setIsNoTarget(false);
          setStep(1);
          setImpactData([]);
          fetchFlashcards();
        } else {
          notifyCustom(res.message || "Failed to assign/unassign flashcards", "danger");
        }
      })
      .catch((err) => {
        console.error("Error saving/updating assignments:", err);
        notifyCustom("An error occurred. Please try again.", "danger");
      })
      .finally(() => setSaving(false));
  };

  const handleNextStep = () => {
    if (!selected.length) {
      alert("Please select at least one flashcard");
      return;
    }

    // No target: assign immediately from step 1 (skip impact review)
    if (isNoTarget) {
      handleAssign();
      return;
    }

    setLoadingImpact(true);
    postDataAPI({
      serviceName: "server_data",
      op: "review-impact-server-target",
      server_id: server?.server_id,
      flash_card_id: selected,
      target_duration: isSpecialOfTheDay
        ? "0"
        : (targetDuration ? targetDuration.replace(/\D/g, "") : ""),
      target_percent: isNoTarget
          ? null
          : (increasePeriod ? increasePeriod.replace(/\D/g, "") : "")
    })
      .then((res) => {
        if (res.status === "ok") {
          setImpactData(res.data || []);
          setStep(2);
        } else {
          alert(res.message || "Failed to fetch review impact targets");
        }
      })
      .catch((err) => {
        console.error("Error fetching impact data:", err);
        alert("An error occurred while fetching impact data.");
      })
      .finally(() => setLoadingImpact(false));
  };

  if (!server) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" backdrop="static">
      <ModalHeader toggle={toggle}>
        {activeTab === TAB_NON_ASSIGNED && step === 2
          ? "Review Impact"
          : `Assign Trainings to ${server?.name}`}
        <div className="text-muted small">
          {activeTab === TAB_NON_ASSIGNED && step === 2
            ? "Please review the targets and expected impact before assigning."
            : "Select flashcards to assign or unassign"}
        </div>
      </ModalHeader>

      <ModalBody>
        {/* Stepper */}
        {isDishAssignTab && (
          <div className="d-flex align-items-center justify-content-start mb-4 px-2">
            {/* Step 1 */}
            <div className="d-flex align-items-center">
              {step > 1 ? (
                <>
                  <div className="d-flex align-items-center justify-content-center border rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, borderColor: "#405189" }}>
                    <i className="ri-check-line fw-bold" style={{ fontSize: 13, color: "#405189" }}></i>
                  </div>
                  <span className="small fw-semibold" style={{ color: "#405189" }}>Select Trainings</span>
                </>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-center text-white rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, fontSize: 12, fontWeight: "bold", backgroundColor: "#405189" }}>
                    1
                  </div>
                  <span className="fw-bold small" style={{ color: "#405189" }}>Select Trainings</span>
                </>
              )}
            </div>
            
            {/* Divider Line */}
            <div className="mx-3" style={{ flex: "1 1 auto", minWidth: "15px", maxWidth: "60px", height: "2px", backgroundColor: step > 1 ? "#405189" : "#e2e8f0" }}></div>

            {/* Step 2 */}
            <div className="d-flex align-items-center">
              {step === 2 ? (
                <>
                  <div className="d-flex align-items-center justify-content-center text-white rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, fontSize: 12, fontWeight: "bold", backgroundColor: "#405189" }}>
                    2
                  </div>
                  <span className="fw-bold small" style={{ color: "#405189" }}>Review Impact</span>
                </>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-center border rounded-circle me-2" style={{ width: 24, height: 24, minWidth: 24, fontSize: 12, fontWeight: "bold", borderColor: "#ced4da", color: "#6c757d" }}>
                    2
                  </div>
                  <span className="text-muted small" style={{ fontWeight: "400" }}>Review Impact</span>
                </>
              )}
            </div>
          </div>
        )}

        {isDishAssignTab && step === 2 ? (
          <>
            {/* Banner summary card */}
            <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center p-3 mb-4 border rounded-3" style={{ backgroundColor: "rgba(64, 81, 137, 0.03)", borderColor: "rgba(64, 81, 137, 0.15)" }}>
              <div className="d-flex align-items-center mb-3 mb-md-0 flex-grow-1">
                <div className="me-3 flex-shrink-0 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44, minWidth: 44 }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#405189" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" fill="#405189" />
                    <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
                  </svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold text-dark mb-1" style={{ fontSize: "13px", lineHeight: "1.4" }}>Targets will be created automatically when the quiz is completed.</div>
                  <div className="text-muted" style={{ fontSize: "11px" }}>
                    {isSpecialOfTheDay
                      ? "Performance will be measured for Today and compared to the Previous Day."
                      : `Performance will be measured for ${targetDurationLabel} and compared to the previous ${targetDurationLabel}.`}
                  </div>
                </div>
              </div>
              <div className="d-flex flex-wrap flex-shrink-0 responsive-banner-divider" style={{ gap: "12px" }}>
                <div className="responsive-col-item responsive-col-border">
                  <div className="text-muted text-nowrap" style={{ fontSize: "10px", fontWeight: "500", marginBottom: 4 }}>Target Duration</div>
                  <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{targetDurationLabel}</div>
                </div>
                <div className="responsive-col-item responsive-col-border">
                  <div className="text-muted text-nowrap" style={{ fontSize: "10px", fontWeight: "500", marginBottom: 4 }}>Increase Over Previous</div>
                  <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{increasePeriod}</div>
                </div>
                <div className="responsive-col-item">
                  <div className="text-muted text-nowrap" style={{ fontSize: "10px", fontWeight: "500", marginBottom: 4 }}>Complete By</div>
                  <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>{formatDateForDisplay(completionDate)}</div>
                </div>
              </div>
            </div>

            {/* Impact Summary Title */}
            <h6 className="fw-bold text-dark mb-3">Impact Summary</h6>

            {/* Dynamic Cards Grid */}
            <Row className="g-2 mb-4">
              <Col sm="6" md="3">
                <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(64, 81, 137, 0.08)", color: "#405189" }}>
                    <i className="ri-book-open-line" style={{ fontSize: 20 }}></i>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Trainings Assigned</div>
                    <div className="fw-bold text-dark" style={{ fontSize: "20px", lineHeight: 1.2 }}>{selected.length}</div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>Flashcards</div>
                  </div>
                </div>
              </Col>
              <Col sm="6" md="3">
                <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(10, 191, 115, 0.08)", color: "#0abf73" }}>
                    <i className="ri-line-chart-line" style={{ fontSize: 20 }}></i>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Expected Target Revenue</div>
                    <div className="fw-bold text-dark" style={{ fontSize: "20px", lineHeight: 1.2 }}>{currency} {Number(totalExpectedRevenue || 0).toFixed(2)}</div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>Over {targetDurationLabel}</div>
                  </div>
                </div>
              </Col>
              <Col sm="6" md="3">
                <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(147, 51, 234, 0.08)", color: "#9333ea" }}>
                    <i className="ri-percent-line" style={{ fontSize: 20 }}></i>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Increase Over Previous</div>
                    <div className="fw-bold" style={{ fontSize: "20px", lineHeight: 1.2, color: "#9333ea" }}>{increasePeriod}</div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>Performance Uplift</div>
                  </div>
                </div>
              </Col>
              <Col sm="6" md="3">
                <div className="border rounded-3 p-3 d-flex align-items-center h-100 bg-white">
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3" style={{ width: 48, height: 48, minWidth: 48, backgroundColor: "rgba(247, 144, 9, 0.08)", color: "#f79009" }}>
                    <i className="ri-timer-line" style={{ fontSize: 20 }}></i>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="text-muted" style={{ fontSize: "12px", fontWeight: "500", marginBottom: 2 }}>Baseline Period</div>
                    <div className="fw-bold text-dark" style={{ fontSize: "20px", lineHeight: 1.2 }}>{baselinePeriodLabel}</div>
                    <div className="text-muted" style={{ fontSize: "11px" }}>
                      {isSpecialOfTheDay ? "Previous Day" : `Previous ${baselinePeriodLabel}`}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Generated Targets Title */}
            <h6 className="fw-bold text-dark mb-3">Generated Targets</h6>

            {/* Table of Targets */}
            <div className="table-responsive border rounded-3 mb-3">
              <Table hover className="align-middle mb-0 bg-white" style={{ tableLayout: "fixed", width: "100%", minWidth: "750px" }}>
                <colgroup>
                  <col style={{ width: "5%" }} />
                  <col style={{ width: "28%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "24%" }} />
                </colgroup>
                <thead className="table-light">
                  <tr style={{ fontSize: "11px", color: "#6c757d" }}>
                    <th className="ps-3 text-start fw-semibold">#</th>
                    <th className="text-start fw-semibold">Flashcard</th>
                    <th className="text-center fw-semibold">
                      <div>{isSpecialOfTheDay ? "Previous Day" : `Previous ${baselinePeriodLabel}`}</div>
                      <div className="fw-normal" style={{ fontSize: "10px", textTransform: "none" }}>(Units Sold)</div>
                    </th>
                    <th className="text-center fw-semibold">
                      <div>Target</div>
                      <div className="fw-normal" style={{ fontSize: "10px", textTransform: "none" }}>({targetDurationLabel})</div>
                    </th>
                    <th className="text-center fw-semibold">
                      <div>Expected Lift</div>
                      <div className="fw-normal" style={{ fontSize: "10px", textTransform: "none" }}>(Units)</div>
                    </th>
                    <th className="text-center fw-semibold">
                      <div>Target Revenue</div>
                      <div className="fw-normal text-center" style={{ fontSize: "10px", textTransform: "none" }}>({currency})</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.rows.map((row) => (
                    <tr key={row.index} style={{ fontSize: "13px" }}>
                      <td className="ps-3 text-start text-muted">{row.index}</td>
                      <td className="text-start fw-semibold text-dark">{row.name}</td>
                      <td className="text-center">{row.previousUnits}</td>
                      <td className="text-center">{row.targetUnits}</td>
                      <td className="text-center">{row.expectedLift}</td>
                      <td className="text-center pe-3 fw-bold" style={{ color: "#212529" }}>
                        {currency} {Number(row.targetRevenue || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Expected Lift Green Message Box */}
            <div className="d-flex align-items-center p-3 mb-3 border rounded-3" style={{ backgroundColor: "rgba(10, 191, 115, 0.05)", borderColor: "rgba(10, 191, 115, 0.15)", color: "#0abf73" }}>
              <i className="ri-line-chart-line me-2 fs-5"></i>
              <span className="small fw-semibold">
                This training is expected to generate an additional {currency} {Number(tableRows.totalRevenueUplift || 0).toFixed(2)} in revenue over {isSpecialOfTheDay ? "Today" : `the next ${targetDurationLabel}`}.
              </span>
            </div>
          </>
        ) : (
          <>
            <Nav tabs className="mb-3">
              {tabConfig.map((tab) => (
                <NavItem key={tab.key}>
                  <NavLink
                    href="#"
                    className={activeTab === tab.key ? "active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab(tab.key);
                      setSelected([]);
                      setStep(1);
                    }}
                  >
                    {tab.label} ({categorizedCards[tab.key]?.length || 0})
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <div style={{ maxHeight: "350px", overflowY: "auto", overflowX: "hidden" }} className="mb-3 px-1">
              {loading ? (
                <p className="text-center text-muted">Loading flashcards...</p>
              ) : visibleCards.length === 0 ? (
                <p className="text-center text-muted py-4">
                  {activeTab === TAB_SCENARIO_ASSIGNED || activeTab === TAB_SCENARIO_NON_ASSIGNED
                    ? "No scenarios in this category."
                    : "No flashcards in this category."}
                </p>
              ) : (
                <Row className="g-3">
                  {visibleCards.map((card) => {
                    const id = String(card.flash_id);
                    const active = selected.includes(id);

                    const completionDateLabel =
                      activeTab === TAB_COMPLETED
                        ? quizMap.get(id)?.quiz_completed_date || ""
                        : card.completion_date || "";

                    const quizItem = quizMap.get(id);
                    const score = quizItem ? Number(quizItem.score || 0) : 0;
                    const totalQuestions = quizItem ? Number(quizItem.total_questions || 0) : 0;
                    const quizScore = quizItem ? `${score}/${totalQuestions}` : null;
                    const quizPercentage = totalQuestions > 0 ? (score / totalQuestions) * 100 : 0;
                    const quizStatus = quizPercentage >= 50 ? "success" : "danger";

                    return (
                      <Col md="4" key={id}>
                        <div
                          onClick={() => toggleCard(id)}
                          className={`border rounded p-3 position-relative ${
                            active ? "border-primary bg-light" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                        >
                          <h6 className="mb-1">{card.item_name}</h6>
                          <Badge color={isScenarioCard(card) ? "danger" : "primary"}>
                            {isScenarioCard(card) ? "Scenario" : card.course_name}
                          </Badge>
                          
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <div>
                              {activeTab === TAB_COMPLETED && quizScore !== null && (
                                <Badge color={quizStatus} pill>
                                  Score: {quizScore}
                                </Badge>
                              )}
                            </div>
                            {completionDateLabel && (
                              <Badge
                                color={activeTab === TAB_COMPLETED ? "success" : "info"}
                              >
                                {activeTab === TAB_COMPLETED
                                  ? "Completed:"
                                  : "Due:"}{" "}
                                {completionDateLabel}
                              </Badge>
                            )}
                          </div>

                          {active && (
                            <i
                              className="ri-check-line text-primary position-absolute"
                              style={{ top: 10, right: 10, fontSize: 18 }}
                            />
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              )}
            </div>

            {isDishAssignTab && (
              <>
                <hr className="my-3 text-muted" />
                <div className="mb-2">
                  <div className="d-flex align-items-center mb-3">
                    <span className="fw-bold text-dark fs-5">Training Settings</span>
                    <span className="mx-2 text-muted">|</span>
                    <span className="text-muted small">Targets are automatically created when the quiz is completed.</span>
                  </div>
                  <Row className="g-3">
                    <Col md="4">
                      <Label className="text-muted small fw-bold">Complete Training By</Label>
                      <div className="position-relative d-flex align-items-center">
                        <i className="ri-calendar-todo-line position-absolute ms-3 text-muted" style={{ zIndex: 10, fontSize: 16 }}></i>
                        <Flatpickr
                          key={isSpecialOfTheDay ? "special-today" : "normal"}
                          className="form-control ps-5"
                          value={completionDate}
                          onChange={(dates) => setCompletionDate(dates[0] || null)}
                          options={{
                            dateFormat: "d M Y",
                            minDate: isSpecialOfTheDay ? today : tomorrow,
                            allowInput: !isSpecialOfTheDay,
                            clickOpens: !isSpecialOfTheDay
                          }}
                          disabled={isSpecialOfTheDay}
                          placeholder="Select Completion Date"
                        />
                      </div>
                      <div className="form-check mt-2">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="specialOfTheDay"
                          checked={isSpecialOfTheDay}
                          disabled={isNoTarget}
                          onChange={(e) => handleSpecialOfTheDayChange(e.target.checked)}
                        />
                        <Label className="form-check-label small" htmlFor="specialOfTheDay">
                          Specials of the day (today)
                        </Label>
                      </div>
                      <div className="form-check mt-1">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="noTarget"
                          checked={isNoTarget}
                          disabled={isSpecialOfTheDay}
                          onChange={(e) => handleNoTargetChange(e.target.checked)}
                        />
                        <Label className="form-check-label small" htmlFor="noTarget">
                          No target
                        </Label>
                      </div>
                    </Col>
                    {!isNoTarget && (
                      <>
                        <Col md="4">
                          <Label className="text-muted small fw-bold">Target Duration (after quiz completion)</Label>
                          <Input
                            type="select"
                            className="form-select"
                            value={targetDuration}
                            onChange={(e) => setTargetDuration(e.target.value)}
                            disabled={isSpecialOfTheDay}
                          >
                            {isSpecialOfTheDay && (
                              <option value={specialTargetDuration}>
                                Today (0 days)
                              </option>
                            )}
                            {targetDurationOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Input>
                        </Col>
                        <Col md="4">
                          <Label className="text-muted small fw-bold">Increase over Previous Period</Label>
                          <Input
                            type="select"
                            className="form-select"
                            value={increasePeriod}
                            onChange={(e) => setIncreasePeriod(e.target.value)}
                          >
                            {increasePeriodOptions.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Input>
                        </Col>
                      </>
                    )}
                  </Row>
                </div>
              </>
            )}
          </>
        )}
      </ModalBody>

      {isDishAssignTab && step === 2 ? (
        <ModalFooter className="w-100 flex-column align-items-stretch border-top-0">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center w-100 mb-3 gap-2">
            <Button outline onClick={() => setStep(1)} className="d-flex align-items-center justify-content-center gap-1">
              <i className="ri-arrow-left-line"></i> Back
            </Button>
            <Button
              color="primary"
              onClick={handleAssign}
              disabled={saving}
              className="d-flex align-items-center justify-content-center gap-1"
            >
              {saving ? "Assigning..." : "Assign Trainings & Targets"}
            </Button>
          </div>
          <div className="text-muted small d-flex align-items-start gap-1 pt-2 border-top" style={{ fontSize: "11px" }}>
            <i className="ri-shield-check-line text-success flex-shrink-0" style={{ fontSize: "14px", marginTop: "1px" }}></i>
            <span>
              Targets will be created automatically when the quiz is completed by {server?.name}.
            </span>
          </div>
        </ModalFooter>
      ) : (
        <ModalFooter className="w-100 justify-content-end">
          <div>
            <Button outline onClick={toggle}>
              Cancel
            </Button>
            {isDishAssignTab ? (
              <Button
                color="primary"
                className="ms-2"
                onClick={isNoTarget ? handleAssign : handleNextStep}
                disabled={selected.length === 0 || loadingImpact || saving}
              >
                {isNoTarget
                  ? (saving ? "Assigning..." : "Assign")
                  : (loadingImpact ? "Loading..." : "Next")}
              </Button>
            ) : isScenarioAssignTab ? (
              <Button
                color="primary"
                className="ms-2"
                onClick={handleAssign}
                disabled={saving || selected.length === 0}
              >
                {saving ? "Assigning..." : "Assign"}
              </Button>
            ) : (
              <Button
                color="primary"
                className="ms-2"
                onClick={handleAssign}
                disabled={saving || selected.length === 0}
              >
                {saving ? "Unassigning..." : "Unassign Selected"}
              </Button>
            )}
          </div>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default AssignTrainingsModal;
